'use client';

/**
 * Origin token lifecycle, browser side.
 *
 * The page fetches a token once on load and sends it with every translate call.
 * Each response carries a replacement, so the value on the wire keeps changing.
 *
 * Two things this must get right, because both are invisible when they break:
 *
 *   • **Refresh on 403.** Tokens expire (20 min). Somebody who leaves the tab
 *     open over lunch would otherwise come back to a translator that silently
 *     refuses to work. On a 403 we mint a new token and retry ONCE — enough to
 *     recover from expiry, and bounded so a genuine rejection cannot loop.
 *   • **One in-flight fetch.** Several debounced translations can fail at the
 *     same instant; without sharing the pending promise they would each mint a
 *     token and the last write would win, leaving the others stale.
 */

const API = process.env.NEXT_PUBLIC_API_BASE ?? '';

let token: string | null = null;
let pending: Promise<string | null> | null = null;

export function currentToken(): string | null {
  return token;
}

/** Store the replacement that came back with a translation. */
export function adoptToken(next: string | undefined | null): void {
  if (typeof next === 'string' && next.length > 0) token = next;
}

/** Fetch a fresh token. Concurrent callers share one request. */
export function refreshToken(): Promise<string | null> {
  if (pending) return pending;

  pending = fetch(`${API}/api/session/token`, {
    method: 'POST',
    // The cookie is half of what the token is bound to — without it the server
    // mints a token for a different session than the one we will send from.
    credentials: 'same-origin',
  })
    .then(async (res) => {
      if (!res.ok) return null;
      const body = (await res.json()) as { token?: string };
      token = body.token ?? null;
      return token;
    })
    .catch(() => null)
    .finally(() => {
      pending = null;
    });

  return pending;
}

/** Called once when the app mounts. */
export function bootstrapToken(): void {
  if (!token) void refreshToken();
}
